/**
 * OrderBookTiles — the current node's header cards (Value · Cost · GM £ · GM %), straight
 * from the served totals. Value spans every line; Cost/GM are costed-only, so a partly-priced
 * node says so via the coverage note rather than diluting margin. Null cost → em-dash, never
 * a fabricated figure (§3).
 *
 * `costNote` is the caller's, not derived here, because the two facts describe their cost
 * differently: the order book names the BASIS the reader picked, and margin names what the
 * one served cost is MADE OF (viewmodels/marginCoverage.costNote — one entity's basis at a
 * site, both entities said as a blend at Group).
 */

import React from 'react'
import { formatCurrency } from '../utils/formatters'
import { EM_DASH } from './kit/cellFormat'
import type { BreakdownTiles } from '../viewmodels/orderBook'

const CARD = 'border border-line rounded-xl bg-panel px-4 py-3.5'
const gbp = (v: number | null): string => (v === null ? EM_DASH : formatCurrency(v, '£', 0))
const pct = (v: number | null): string => (v === null ? EM_DASH : `${v.toFixed(1)}%`)

function Tile({ label, value, note, sub, tone, warn }: {
  label: string
  value: string
  note?: string
  /** A second, equally plain line — where one note cannot carry the whole population. */
  sub?: string
  tone?: string
  /** What this figure does NOT cover, when that is an omission rather than a nuance. */
  warn?: string
}): React.JSX.Element {
  return (
    <div className={CARD}>
      <span className="font-mono text-[10px] tracking-[.08em] uppercase text-mut2">{label}</span>
      <div className={`text-[24px] font-bold tracking-[-.02em] mt-1.5 tabular-nums ${tone ?? 'text-ink'}`}>{value}</div>
      {note && <div className="mt-1 font-mono text-[9.5px] text-mut2">{note}</div>}
      {sub && <div className="mt-0.5 font-mono text-[12px] font-medium text-mut">{sub}</div>}
      {warn && <div className="font-mono text-[9.5px] text-amber">⚠ {warn}</div>}
    </div>
  )
}

/** The Sage figure beside each of ours, in small print. THE POINT OF THE PAGE, in four
 * numbers: our cards come from the ERP invoice and Sage is the ledger, they share the invoice
 * date, so they must agree. Optional because the order book has no ledger counterpart —
 * it is demand, and demand is not posted anywhere. */
export interface SageSubs {
  value?: string
  cost?: string
  gm?: string
  gmPct?: string
}

export default function OrderBookTiles({ tiles, costNote, sage }: {
  tiles: BreakdownTiles
  costNote: string
  sage?: SageSubs
}): React.JSX.Element {
  const gmTone = tiles.gm === null ? undefined : tiles.gm < 0 ? 'text-bad' : 'text-good'
  // ⚠ ONE EXPLANATORY LINE PER CARD, AT MOST (Mike, 24 Aug 2026). A card carried a label, a
  // figure, a note AND a subtitle, and the note was the one nobody needed — "816 lines ·
  // billed", "on costed value". Where a LEDGER figure is available it takes that slot,
  // because a reader comparing us to Sage is doing the only comparison the card invites.
  // The order book has no ledger to answer to and keeps its notes: its cost really does need
  // saying which basis it is, since that surface still has a toggle.
  const ledger = sage !== undefined
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile
        label="Value Generated"
        value={gbp(tiles.value)}
        note={ledger ? undefined : `${tiles.lineCount.toLocaleString('en-GB')} lines`}
        sub={sage?.value}
      />
      <Tile
        label="Cost of Production"
        value={gbp(tiles.cost)}
        note={ledger ? undefined : costNote}
        sub={sage?.cost}
        warn={offLensNote(tiles)}
      />
      <Tile label="Gross Margin (£)" value={gbp(tiles.gm)} sub={sage?.gm} tone={gmTone} />
      <Tile
        label="Gross Margin (%)"
        value={pct(tiles.gmPct)}
        note={ledger ? undefined : 'on costed value'}
        sub={sage?.gmPct}
        tone={gmTone}
      />
    </div>
  )
}

/** The cost this figure LEAVES OUT because it sits on the other lens. ORDER BOOK ONLY: that
 * fact carries a part standard AND a job actual on the same row, so the card has to say which
 * one it is showing. The margin fact serves one cost and passes null. */
function offLensNote(tiles: BreakdownTiles): string | undefined {
  if (!tiles.offLens) return undefined
  return `excludes ${gbp(tiles.offLens.cost)} costed on ${tiles.offLens.label}`
}
